import React from 'react';

const normalize = (value) => (value || '').toString().trim().toLowerCase();
const displayName = (value) => {
    if (!value || typeof value !== 'object') return value || '';
    return value.nom || value.name || value.libelle || value.label || value.code || '';
};

export const getUserEntity = (user) =>
    user?.entityName || user?.directionName || user?.direction || user?.entityOrgName || user?.entiteNom || user?.department || '';

export const getUserPoste = (user) =>
    user?.poste || user?.jobName || user?.userJobName || user?.jobNom || user?.roleName || '';

export const getSkillName = (skill) =>
    displayName(skill?.skill) || skill?.skillNom || skill?.skillName || skill?.skillCode || skill?.competenceNom || skill?.competenceName || skill?.nom || '';

export const getSkillType = (skill) =>
    displayName(skill?.skillType) || skill?.skillTypeNom || skill?.typeCompetence || displayName(skill?.type) || skill?.category || '';

export const getSkillDomaine = (skill) =>
    skill?.domaineNom || skill?.domaineName || skill?.domainName || displayName(skill?.domaine) || displayName(skill?.domain) || skill?.skillDomaineNom || '';

export const getSkillTheme = (skill) =>
    skill?.themeNom || skill?.themeName || displayName(skill?.theme) || skill?.skillThemeNom || '';

export const toRadarSkill = (n, extra = {}) => ({
    id: n.skillId || n.id,
    skill: getSkillName(n),
    obtenu: n.scoreObtenu || n.scoreRf || n.scoreN2 || n.scoreManager || n.scoreCollab || 0,
    requis: n.scoreRequis || 3,
    skillType: getSkillType(n),
    domaineNom: getSkillDomaine(n),
    themeNom: getSkillTheme(n),
    ...extra,
});

export const skillMatchesRadarFilters = (skill, filters = {}) => {
    const domaine = normalize(filters.domaine);
    const theme = normalize(filters.theme);
    const competence = normalize(filters.competence);

    return (!domaine || normalize(getSkillDomaine(skill)) === domaine)
        && (!theme || normalize(getSkillTheme(skill)) === theme)
        && (!competence || normalize(getSkillName(skill)) === competence);
};

export const filterRadarData = (skills, filters = {}) =>
    (skills || []).filter((skill) => skillMatchesRadarFilters(skill, filters));

export const userMatchesRadarFilters = (user, filters = {}) => {
    const search = normalize(filters.search);
    const entity = normalize(filters.entity);
    const poste = normalize(filters.poste);
    const hasSkillFilter = !!(filters.domaine || filters.theme || filters.competence);

    const matchSearch = !search || normalize(`${user?.nom || ''} ${user?.userFullName || ''} ${getUserPoste(user)}`).includes(search);
    const matchEntity = !entity || normalize(getUserEntity(user)) === entity;
    const matchPoste = !poste || normalize(getUserPoste(user)) === poste;
    const matchSkills = !hasSkillFilter || (user?.niveaux || []).some((skill) => skillMatchesRadarFilters(skill, filters));

    return matchSearch && matchEntity && matchPoste && matchSkills;
};

export const filterUsersByRadarFilters = (users, filters = {}) =>
    (users || []).filter((user) => userMatchesRadarFilters(user, filters));

const uniqueOptions = (values) =>
    Array.from(new Set(values.filter(Boolean).map((value) => value.toString().trim()).filter(Boolean)))
        .sort((a, b) => a.localeCompare(b));

const optionName = (item) => item?.nom || item?.name || item?.libelle || item?.label || '';

export const buildDomainThemeRadarOptions = (domaines = [], themes = []) => ({
    domaines: uniqueOptions((domaines || []).map(optionName)),
    themes: uniqueOptions((themes || []).map(optionName)),
});

export const isSkillsCampaign = (campaign) =>
    String(campaign?.typeCampagne || campaign?.type || '').toUpperCase() === 'SKILLS';

export const buildRadarFilterOptions = (users = []) => {
    const skills = users.flatMap((user) => user?.niveaux || []);
    return {
        entities: uniqueOptions(users.map(getUserEntity)),
        postes: uniqueOptions(users.map(getUserPoste)),
        domaines: uniqueOptions(skills.map(getSkillDomaine)),
        themes: uniqueOptions(skills.map(getSkillTheme)),
        competences: uniqueOptions(skills.map(getSkillName)),
    };
};

const FilterSelect = ({ label, value, onChange, options, placeholder }) => (
    <label className="eval-radar-filter-field">
        <span>{label}</span>
        <select value={value || ''} onChange={(event) => onChange(event.target.value)}>
            <option value="">{placeholder || 'Tous'}</option>
            {(options || []).map((option) => (
                <option key={option.value || option} value={option.value || option}>{option.label || option}</option>
            ))}
        </select>
    </label>
);

export const RadarFiltersPanel = ({
    filters,
    setFilters,
    options,
    showUserFilters = true,
    collaboratorValue,
    onCollaboratorChange,
    collaboratorOptions = [],
    campaignValue,
    onCampaignChange,
    campaignOptions = [],
    campaignPlaceholder = 'Toutes les campagnes',
    hideEntityFilter = false,
    className = '',
}) => {
    const update = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
    const reset = () => {
        setFilters((prev) => ({
            ...prev,
            entity: '',
            poste: '',
            domaine: '',
            theme: '',
            competence: '',
        }));
        if (onCollaboratorChange) onCollaboratorChange('');
    };

    return (
        <section className={`eval-radar-filters-card${showUserFilters ? '' : ' eval-radar-filters-card--compact'}${className ? ` ${className}` : ''}`}>
            <div className="eval-radar-filters-title">
                <span />
                Filtres de recherche
            </div>
            <div className="eval-radar-filters-grid">
                {showUserFilters && (
                    <>
                        {onCampaignChange && (
                            <FilterSelect
                                label="Campagne"
                                value={campaignValue}
                                onChange={onCampaignChange}
                                options={(campaignOptions || []).map((campaign) => ({
                                    value: campaign.id?.toString(),
                                    label: `${campaign.nom || campaign.name || 'Campagne'}${campaign.anneeCampagne ? ` (${campaign.anneeCampagne})` : ''}`,
                                }))}
                                placeholder={campaignPlaceholder}
                            />
                        )}
                        {!hideEntityFilter && (
                            <FilterSelect label="Entite" value={filters.entity} onChange={(value) => update('entity', value)} options={options.entities} />
                        )}
                        <FilterSelect label="Poste" value={filters.poste} onChange={(value) => update('poste', value)} options={options.postes} />
                        {onCollaboratorChange && (
                            <FilterSelect
                                label="Collaborateur"
                                value={collaboratorValue}
                                onChange={onCollaboratorChange}
                                options={collaboratorOptions}
                                placeholder="Tous les collaborateurs"
                            />
                        )}
                    </>
                )}
                {!showUserFilters && onCampaignChange && (
                    <FilterSelect
                        label="Campagne"
                        value={campaignValue}
                        onChange={onCampaignChange}
                        options={(campaignOptions || []).map((campaign) => ({
                            value: campaign.id?.toString(),
                            label: `${campaign.nom || campaign.name || 'Campagne'}${campaign.anneeCampagne ? ` (${campaign.anneeCampagne})` : ''}`,
                        }))}
                        placeholder={campaignPlaceholder}
                    />
                )}
                <FilterSelect label="Domaine" value={filters.domaine} onChange={(value) => update('domaine', value)} options={options.domaines} />
                <FilterSelect label="Theme" value={filters.theme} onChange={(value) => update('theme', value)} options={options.themes} />
                <FilterSelect label="Competence" value={filters.competence} onChange={(value) => update('competence', value)} options={options.competences} />
            </div>
            <div className="eval-radar-filters-actions">
                <button type="button" onClick={reset}>Reinitialiser</button>
            </div>
        </section>
    );
};
